import React, { useState } from 'react'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ReplyIcon from '@mui/icons-material/Reply'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove'
import LockIcon from '@mui/icons-material/Lock'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import { BookmarkAdd } from '@mui/icons-material'
import BookmarkRemoveIcon from '@mui/icons-material/BookmarkRemove'
import useFavoriteBacktest from '../../hooks/useFavoriteBacktest'
import { BacktestFolder } from '../../hooks/useBacktestFolders'
import { useApplicationSuiteContext } from '../../contexts/ApplicationSuiteProvider'

type MenuView = 'main' | 'folders'

function BacktestActionsMenu({
  backtest,
  canView,
  hideBacktestLoading,
  onShare,
  onHide,
  onToggleVisibility,
  updateVisibilityLoading,
  folders,
  onMoveToFolder,
}: {
  backtest: any
  canView?: boolean
  hideBacktestLoading: boolean
  onShare: () => void
  onHide: () => void
  onToggleVisibility: (nextIsPublic: boolean) => void | Promise<void>
  updateVisibilityLoading: boolean
  folders?: BacktestFolder[]
  onMoveToFolder?: (folderId: string | null) => void | Promise<void>
}) {
  const { usermeta, canPrivatePatterns } = useApplicationSuiteContext()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [view, setView] = useState<MenuView>('main')
  const [isMoving, setIsMoving] = useState(false)
  const { isFavorited, toggleFavorite } = useFavoriteBacktest(backtest)

  const ownsBacktest = backtest.gcid === usermeta.gcid
  const isPublic = !!backtest.backtestSettings.isPublic
  // A private backtest can only be shared once its owner makes it public — if
  // the current viewer doesn't own it, there's no path to fix that, so the
  // option is hidden entirely rather than shown disabled with no way out.
  const showShareItem = isPublic || ownsBacktest
  const showVisibilityToggleItem = ownsBacktest && canPrivatePatterns

  const hasFolders = !!folders && folders.length > 0 && !!onMoveToFolder
  const currentFolderId =
    folders?.find((folder) => folder.backtestIds?.includes(backtest.id))?.id ?? null

  function closeMenu() {
    setAnchorEl(null)
    setView('main')
  }

  async function handleFavoriteClick() {
    closeMenu()
    // Folder cleanup on unfavorite is handled inside useFavoriteBacktest
    // itself (via FolderMembershipContext), so it applies uniformly to every
    // unfavorite entry point, not just this menu.
    await toggleFavorite()
  }

  function handleShareClick() {
    closeMenu()
    onShare()
  }

  function handleHideClick() {
    closeMenu()
    onHide()
  }

  function handleToggleVisibilityClick() {
    closeMenu()
    onToggleVisibility(!isPublic)
  }

  async function handleMoveToFolder(folderId: string | null) {
    if (!onMoveToFolder) return
    closeMenu()
    setIsMoving(true)
    try {
      await onMoveToFolder(folderId)
    } finally {
      setIsMoving(false)
    }
  }

  const mainItems = [
    <MenuItem
      key="favorite"
      onClick={handleFavoriteClick}
      data-tour-id="favorite-backtest-menu-item"
    >
      <ListItemIcon>
        {isFavorited ? <BookmarkRemoveIcon fontSize="small" /> : <BookmarkAdd fontSize="small" />}
      </ListItemIcon>
      <ListItemText>{isFavorited ? 'Remove from Favorites' : 'Add to Favorites'}</ListItemText>
    </MenuItem>,
    hasFolders && (
      <MenuItem key="move-to-folder" onClick={() => setView('folders')} disabled={isMoving}>
        <ListItemIcon>
          <DriveFileMoveIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Move to folder</ListItemText>
      </MenuItem>
    ),
    showShareItem && (
      <MenuItem
        key="share"
        onClick={handleShareClick}
        disabled={!isPublic}
        data-tour-id="share-backtest-menu-item"
      >
        <ListItemIcon>
          <ReplyIcon fontSize="small" sx={{ transform: 'scaleX(-1)' }} />
        </ListItemIcon>
        <ListItemText>
          {isPublic ? 'Share backtest' : 'Make backtest public before sharing'}
        </ListItemText>
      </MenuItem>
    ),
    showVisibilityToggleItem && (
      <MenuItem
        key="toggle-visibility"
        onClick={handleToggleVisibilityClick}
        disabled={updateVisibilityLoading}
        data-tour-id="toggle-visibility-menu-item"
      >
        <ListItemIcon>
          {updateVisibilityLoading ? (
            <CircularProgress size={20} />
          ) : isPublic ? (
            <LockIcon fontSize="small" />
          ) : (
            <LockOpenIcon fontSize="small" />
          )}
        </ListItemIcon>
        <ListItemText>{isPublic ? 'Make backtest private' : 'Make backtest public'}</ListItemText>
      </MenuItem>
    ),
    canView && (
      <MenuItem key="hide" onClick={handleHideClick} disabled={hideBacktestLoading}>
        <ListItemIcon>
          {hideBacktestLoading ? (
            <CircularProgress size={20} />
          ) : (
            <VisibilityOff fontSize="small" color="error" />
          )}
        </ListItemIcon>
        <ListItemText primaryTypographyProps={{ color: 'error' }}>Hide backtest</ListItemText>
      </MenuItem>
    ),
  ]

  const folderItems = [
    <MenuItem key="back" onClick={() => setView('main')}>
      <ListItemIcon>
        <ArrowBackIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>Back</ListItemText>
    </MenuItem>,
    <MenuItem
      key="no-folder"
      disabled={isMoving}
      selected={currentFolderId === null}
      onClick={() => handleMoveToFolder(null)}
    >
      <ListItemText inset>No Folder</ListItemText>
    </MenuItem>,
    ...(folders ?? []).map((folder) => (
      <MenuItem
        key={folder.id}
        disabled={isMoving}
        selected={currentFolderId === folder.id}
        onClick={() => handleMoveToFolder(folder.id)}
      >
        <ListItemText inset>{folder.name}</ListItemText>
      </MenuItem>
    )),
  ]

  return (
    <>
      <Tooltip title="More actions" placement="top">
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          data-tour-id="backtest-actions-menu-button"
        >
          <MoreVertIcon />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={closeMenu}>
        {view === 'main' ? mainItems : folderItems}
      </Menu>
    </>
  )
}

export default BacktestActionsMenu
